// Phase 1A Step 34 — Live ROLLOVER calculator (orchestrator).
// Verbatim port from handoff/landing-reference/calculator.jsx.
// Sub-components extracted to keep each file under 250 LOC:
//   - CalculatorControls (mode toggle + stake input + chain-length slider)
//   - CalculatorHero     (animated hero number + dual-paths sub-line)
//   - CalculatorChainTrack (week-by-week reveal + bottom meta)
//
// Locked per handoff README "must not change":
//   default GH₵85,598 protected pocket on a GH₵300 / 3.0× / 10-week chain
//   at 50% takeout. Math lives in calc-utils.ts.

import { useMemo } from "react";
import { buildChain } from "./calc-utils.ts";
import { CalculatorChainTrack } from "./CalculatorChainTrack.tsx";
import { CalculatorControls } from "./CalculatorControls.tsx";
import { CalculatorHero } from "./CalculatorHero.tsx";

type Mode = "protected" | "full";

interface CalculatorProps {
  stake: number;
  setStake: (v: number) => void;
  weeks: number;
  setWeeks: (v: number) => void;
  odds: number;
  takeout: number;
  symbol: string;
  mode: Mode;
  setMode: (m: Mode) => void;
}

export function Calculator({
  stake,
  setStake,
  weeks,
  setWeeks,
  odds,
  takeout,
  symbol,
  mode,
  setMode,
}: CalculatorProps): JSX.Element {
  const chain = useMemo(
    () => buildChain(stake, weeks, odds, takeout),
    [stake, weeks, odds, takeout],
  );
  const fullChain = useMemo(() => buildChain(stake, weeks, odds, 0), [stake, weeks, odds]);

  const lastRow = chain[chain.length - 1]!;
  const fullLast = fullChain[fullChain.length - 1]!;

  const heroTarget = mode === "protected" ? lastRow.pocketIfStop : fullLast.win;
  const fullCeiling = fullLast.win;

  return (
    <div style={{ position: "relative" }}>
      <div className="accent-glow" style={{ top: "-100px", right: "-150px", zIndex: 0 }} />

      <div
        className="sz-l-calc-card"
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #131313 0%, #0E0E0E 100%)",
          border: "1px solid var(--border)",
          padding: "28px 32px 32px",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 22,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="live-dot" />
            <span className="eyebrow-muted">Live · Rollover Engine</span>
          </div>
          <div
            className="mono"
            style={{ color: "var(--text-dim)", fontSize: 11, letterSpacing: "0.1em" }}
          >
            v3.04 · {odds.toFixed(2)}× ODDS
          </div>
        </div>

        <CalculatorControls
          stake={stake}
          setStake={setStake}
          weeks={weeks}
          setWeeks={setWeeks}
          takeout={takeout}
          symbol={symbol}
          mode={mode}
          setMode={setMode}
        />

        <CalculatorHero
          mode={mode}
          weeks={weeks}
          stake={stake}
          odds={odds}
          takeout={takeout}
          symbol={symbol}
          heroTarget={heroTarget}
          fullCeiling={fullCeiling}
          chain={chain}
        />

        <CalculatorChainTrack chain={chain} weeks={weeks} mode={mode} />
      </div>

      <div
        className="mono"
        style={{
          position: "absolute",
          top: -22,
          left: 16,
          fontSize: 10,
          letterSpacing: "0.18em",
          color: "var(--text-dim)",
          textTransform: "uppercase",
        }}
      >
        ROLLOVER.SIM &gt; {mode === "protected" ? "PROTECTED PATH" : "FULL ROLLOVER"}
      </div>
    </div>
  );
}
